import { useQuery } from "@apollo/client/react";
import { Box, IconicButton, Text } from "@nulogy/components";
import DeleteConfirmationModal from "components/common/DeleteConfirmationModal";
import { GridBoxPlannedBreaks } from "components/common/EditableListStyledComponents";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import { GetAllLines } from "hooks/apollo/line/line.gql";
import { removePlannedBreak } from "hooks/apollo/plannedBreak/helpers/handleUpdatePlannedBreaks";
import useDeletePlannedBreak from "hooks/apollo/plannedBreak/useDeletePlannedBreak";
import useGetPlannedBreaks from "hooks/apollo/plannedBreak/useGetPlannedBreaks";
import useDeleteConfirmationModal from "hooks/state/useDeleteConfirmationModal";
import React, { useState } from "react";
import Uuid from "utils/Uuid";
import NewPlannedBreak from "./NewPlannedBreak";
import PlannedBreak from "./PlannedBreak";

export default function PlannedBreaks() {
  const { plannedBreaks, loading: plannedBreaksLoading } = useGetPlannedBreaks();
  const { data: linesData, loading: linesLoading } = useQuery(GetAllLines, { variables: { activeOnly: false } });
  const [newPlannedBreakIds, setNewPlannedBreakIds] = useState([]);
  const { open: openModal, data: deleteData } = useDeleteConfirmationModal();
  const deletePlannedBreak = useDeletePlannedBreak();

  const handleAddNewPlannedBreak = () => {
    setNewPlannedBreakIds([...newPlannedBreakIds, Uuid.generate()]);
  };

  const handleRemoveNewPlannedBreak = (plannedBreakId) => {
    setNewPlannedBreakIds(newPlannedBreakIds.filter((value) => value !== plannedBreakId));
  };

  const handleDelete = (plannedBreak) => {
    openModal(plannedBreak);
  };

  const onDelete = (plannedBreak) =>
    deletePlannedBreak({
      update: (proxy) => {
        removePlannedBreak(plannedBreak.id, proxy);
      },
      variables: { id: plannedBreak.id },
    });

  function renderContent() {
    if (plannedBreaksLoading || linesLoading) return null;

    const allLinesCount = linesData.lines.length;
    const hasPlannedBreaks = newPlannedBreakIds.length > 0 || plannedBreaks.length > 0;

    const addButtonLabel = "Create a new break";
    const deleteMessage = `Deleting ${deleteData.name} will increase the expected quantity of blocks scheduled during that time, while their duration will remain unchanged. Blocks scheduled in the past will not be affected.`;

    return (
      <Box minWidth="1192px" width="100%">
        <DeleteConfirmationModal message={deleteMessage} onDelete={onDelete} title={`Delete ${deleteData.name}?`} />
        <IconicButton
          data-testid="add-button"
          icon="add"
          marginBottom="x2"
          onClick={handleAddNewPlannedBreak}
          type="button"
        >
          {addButtonLabel}
        </IconicButton>
        {hasPlannedBreaks && (
          <Box data-testid="planned-breaks-table">
            <GridBoxPlannedBreaks>
              <Text>Name</Text>
              <Text>Lines</Text>
              <Text>Days</Text>
              <Text>Start time</Text>
              <Text>End time</Text>
            </GridBoxPlannedBreaks>
            <hr style={{ marginBottom: "0px" }} />
            {newPlannedBreakIds.map((newPlannedBreakId) => (
              <NewPlannedBreak
                key={newPlannedBreakId}
                onRemoveNewPlannedBreak={handleRemoveNewPlannedBreak}
                plannedBreakId={newPlannedBreakId}
              />
            ))}
            {plannedBreaks.map((plannedBreak, index) => (
              <PlannedBreak
                allLinesCount={allLinesCount}
                index={index}
                key={plannedBreak.id}
                onDelete={handleDelete}
                plannedBreak={plannedBreak}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <PageWithBreadcrumbs
      className="spec-planned-breaks-index-page"
      description="Create, edit, or delete your site’s planned breaks on this page."
      title="Planned breaks"
    >
      {renderContent()}
    </PageWithBreadcrumbs>
  );
}
